import { useMemo, useState, type ReactNode } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useResourceKit } from '@resource-kit/core/react';
import { failLoud } from '@resource-kit/core';
import { Plus } from 'lucide-react';
import type { ColumnDef } from '@tanstack/react-table';
import { PageShell } from '../primitives/PageShell';
import { ListView } from '../list/ListView';
import { useKitIsPhone } from '../list/useKitIsPhone';
import { PageHeader } from '../primitives/PageHeader';
import { Button } from '../primitives/Button';
import { createColumns } from '../columns';
import { resourceKeys } from '@resource-kit/controllers';
import type { ColumnSchema } from '@resource-kit/model';
import type { FilterWhitelist } from '@resource-kit/controllers';
import type { AnyFieldDef } from '@resource-kit/model';
import type { ResourceBundle, ResourceFilterOptions } from '@resource-kit/controllers/react';
import { QuickFilters, assertQuickFiltersWhitelisted } from './QuickFilters';
import { ResourceErrorBoundary } from './ResourceErrorBoundary';
import { ResourceFormDialog } from './ResourceFormDialog';
import { ResourceDetailSurface, type DetailTarget } from './ResourceDetailSurface';
import { RowActionsCell } from './RowActionsCell';
import { RowBusyProvider, useRowBusyRegistry } from '@resource-kit/controllers/react';
import { useRemoteList } from '@resource-kit/controllers/react';
import { useResourceBulkActions } from '@resource-kit/controllers/react';

interface ResourcePageProps<T, F extends Record<string, AnyFieldDef>> {
  bundle: ResourceBundle<T, F>;
  /**
   * Bespoke page-level action(s) rendered in the PageHeader's actions region
   * (same row as the title, right-aligned) alongside the generic create/bulk
   * affordances. This is the seam for pages that compose ResourcePage and need
   * a custom CTA (e.g. MenuPlanListPage's "New plan" dialog opener) — without
   * it callers hand-rolled a `<div className="flex justify-end">` OUTSIDE
   * ResourcePage, which floated the button above the page title.
   */
  headerActions?: ReactNode;
  /**
   * When `false`, ResourcePage renders WITHOUT its own `PageContainer` because
   * an embedding page already supplies one (e.g. WebhookEventsPage wraps a tab
   * strip + this list in a single container). Defaults to `true` so the direct
   * route usages keep their standalone page frame. (#1392 D1)
   *
   * `host.listTabs` renders beneath the header in this mode too, the same
   * place `PageShell` puts it.
   */
  container?: boolean;
}

interface ResourcePageInnerProps<T, F extends Record<string, AnyFieldDef>> {
  bundle: ResourceBundle<T, F>;
  baseColumns: ColumnDef<T>[];
  detailColumns: ColumnDef<T>[];
  headerActions?: ReactNode;
  container?: boolean;
}

export function ResourcePage<T, F extends Record<string, AnyFieldDef>>({
  bundle,
  headerActions,
  container,
}: ResourcePageProps<T, F>) {
  // The list and drawer are separate surfaces, so build both projections at
  // this composition root and hand them down unchanged.
  // oxlint-disable-next-line react-hooks/exhaustive-deps
  const baseColumns = useMemo(() => createColumns(bundle, { mode: 'list' }), [bundle.host.slug]);
  // oxlint-disable-next-line react-hooks/exhaustive-deps
  const detailColumns = useMemo(
    () => createColumns(bundle, { mode: 'detail' }),
    [bundle.host.slug],
  );
  // Force full remount when navigating between different resource pages.
  // Without this, React reuses the component instance and useState retains
  // stale state (e.g. defaultConditions from the previous resource).
  return (
    <ResourcePageInner
      key={bundle.host.slug}
      bundle={bundle}
      baseColumns={baseColumns}
      detailColumns={detailColumns}
      headerActions={headerActions}
      container={container}
    />
  );
}

/** How long a filter's option set stays fresh — it is roster data, not rows. */
const FILTER_OPTIONS_STALE_MS = 5 * 60 * 1000;

/**
 * Render-time guard, the twin of `assertQuickFiltersWhitelisted` (#2068).
 *
 * A `filterOptions` entry aimed at a column outside the backend whitelist
 * renders a perfectly usable Select whose conditions the wire layer then drops,
 * so the page just looks unfiltered. Fail loud where the typo is written —
 * through `failLoud`, so dev and test throw while a production build and an
 * env-less host get a `console.error` instead of an error boundary (#2189).
 */
function assertFilterOptionsWhitelisted(
  decls: readonly ResourceFilterOptions[],
  filterable: FilterWhitelist | undefined,
  slug: string,
): void {
  if (!filterable) return;
  for (const decl of decls) {
    if (!filterable[decl.fieldId]) {
      failLoud(
        `${slug}: filterOptions field '${decl.fieldId}' is not in the resource's filterable whitelist`,
      );
    }
  }
}

/**
 * Composition root for a resource list page (#1801).
 *
 * It owns nothing but the wiring. Each of the things this file used to hold
 * inline now lives with whoever owns its state:
 *
 *   - query state, fetching, client pagination, export → `useRemoteList`
 *   - selection + bulk dispatch                        → `useResourceBulkActions`
 *   - the record drawer, edit dialog and delete        → `ResourceDetailSurface`
 *   - loading / empty / filtered-empty / error chrome  → `<ListView>` (§6.4)
 *
 * What is left here is genuinely page-shaped: permissions, the header cluster,
 * the quick filters that write into the shared condition state, and the
 * row-click routing decision.
 */

// `ResourcePageInner` is published from `./react` for the host's own test helper
// (`src/__tests__/resource-kit/components-resource/test-utils.tsx`), which
// mounts the inner shell to skip the outer error boundary and column
// derivation. It is not part
// of the supported surface: a host renders `ResourcePage`. A `./testing` subpath
// was considered and rejected in #2170 — one more published entry to keep the
// boundary gate honest about, for one consumer.
//
// The tag below is its own one-line block, immediately above the declaration,
// so that #2169's acceptance probe — a `grep -B1` for the declaration line —
// reads it. Buried mid-docblock the marker is just as true and invisible to the
// one command anyone runs to check for it. This comment deliberately does NOT
// spell the declaration out: the probe would match this line too, and report a
// preceding line that carries no tag.
/** @internal */
export function ResourcePageInner<T, F extends Record<string, AnyFieldDef>>({
  bundle,
  baseColumns,
  detailColumns,
  headerActions,
  container = true,
}: ResourcePageInnerProps<T, F>) {
  // Render-time guard (#1392 D4): a quick filter must write a
  // backend-accepted condition. Runs before any hook so it reports
  // consistently on every render for a misconfigured resource (no
  // hook-order hazard), and it runs in EVERY host — there is no mode
  // predicate here any more. `failLoud` makes the one mode-dependent
  // decision left: throw in dev/test, `console.error` in a production build
  // and in a host whose bundler injects no env (#2189). Checking everywhere
  // buys timing — a filter outside the whitelist is already reported once it
  // is applied, by the banner and the warning `droppedFilterFields` drives;
  // this reports at render instead, and names the declaration.
  if (bundle.host.quickFilters) {
    assertQuickFiltersWhitelisted(
      bundle.host.quickFilters,
      bundle.host.filterable,
      bundle.host.slug,
    );
  }
  if (bundle.host.filterOptions) {
    assertFilterOptionsWhitelisted(
      bundle.host.filterOptions,
      bundle.host.filterable,
      bundle.host.slug,
    );
  }

  const { t, access } = useResourceKit();
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();

  // Shared per-row busy registry (#1416). Owned here so the row-actions cell,
  // the bulk bar, and the detail footer observe ONE busy set.
  const rowBusy = useRowBusyRegistry();

  const list = useRemoteList(bundle);

  // Write-affordance gating (#1152). The resource's `permission` declaration
  // is the single source: a mutating affordance renders only when the caller
  // holds the declared grant. An undeclared slot is NOT gated — it falls back
  // to plain API presence. Missing grants HIDE the control (not disable it):
  // an in-page action has no "it exists" value to a user who can't use it.
  // The grants arrive through the kit's services seam (#2165); the wrapper
  // adds only the "no permission declared" case, which is a schema fact rather
  // than an identity one and therefore stays here.
  const hasGrant = (perm: string | undefined) => perm == null || access.hasGrant(perm);

  const bulk = useResourceBulkActions(bundle, { hasGrant });

  // ─── Columns ───────────────────────────────────────────────────────────
  const queryFields = useMemo(
    () =>
      baseColumns.map((column, index) => {
        const field = column.meta?.field;
        if (field === undefined) {
          throw new Error(`${bundle.host.slug}: column ${index} has no field metadata`);
        }
        return field;
      }),
    [baseColumns, bundle.host.slug],
  );

  // ─── Host-declared filter options (#2068) ──────────────────────────────
  // One query for every declaration, so the hook is unconditional. The result
  // becomes plain `enumValues` on the QueryBuilder schema — the panel keeps
  // its static enum path and never learns to search.
  const filterOptionDecls = bundle.host.filterOptions;
  const filterOptionsQuery = useQuery({
    queryKey: [
      bundle.host.slug,
      'filter-options',
      (filterOptionDecls ?? []).map((d) => d.fieldId).join(','),
    ],
    queryFn: () => Promise.all((filterOptionDecls ?? []).map((d) => d.load())),
    enabled: (filterOptionDecls?.length ?? 0) > 0,
    staleTime: FILTER_OPTIONS_STALE_MS,
  });

  // Loading, failed, and empty all resolve to `undefined`: the column falls
  // back to the derived text filter, which is what the page does today. A
  // half-populated Select would be worse than a box the operator can type in.
  const queryBuilderColumns = useMemo<ColumnSchema[] | undefined>(() => {
    const loaded = filterOptionsQuery.data;
    if (!filterOptionDecls || !loaded) return undefined;
    const extras: ColumnSchema[] = [];
    filterOptionDecls.forEach((decl, index) => {
      const options = loaded[index];
      if (!options || options.length === 0) return;
      // Same id derivation `deriveQueryColumnsFromFields` uses, so the
      // extra lands ON the derived column instead of beside it.
      const derived = queryFields.find((f) => (f.filterId || String(f.key)) === decl.fieldId);
      // Deliberately no `operations` key: ListView merges extras OVER the
      // derived column, so restating it would erase the whitelisted
      // operator set the backend actually accepts.
      extras.push({
        id: decl.fieldId,
        label: decl.label ?? derived?.label ?? decl.fieldId,
        type: 'enum',
        enumValues: options.map((o) => ({
          value: String(o.value),
          label: o.label,
        })),
      });
    });
    return extras.length > 0 ? extras : undefined;
  }, [filterOptionDecls, filterOptionsQuery.data, queryFields]);

  // Row-level actions (#1287). The `__row_actions__` column is appended only
  // when the resource declares `rowActions` AND the viewer holds at least one
  // of their permissions — a pure viewer never gets an empty trailing column.
  // Per-row permission + eligibility gating is owned by `RowActionsCell`.
  const rowActionsColumn = useMemo<ColumnDef<T> | null>(() => {
    const actions = bundle.host.rowActions;
    if (!actions || actions.length === 0) return null;
    if (!actions.some((a) => hasGrant(a.permission))) return null;
    return {
      id: '__row_actions__',
      header: () => null,
      enableSorting: false,
      cell: ({ row }) => (
        <RowActionsCell
          row={row.original}
          rowId={String(row.original[bundle.model.idField])}
          actions={actions}
          slug={bundle.host.slug}
        />
      ),
    };
    // hasGrant closes over `access`; slug identifies the resource.
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [bundle.host.slug, access]);

  const columns = useMemo(
    () => (rowActionsColumn ? [...baseColumns, rowActionsColumn] : baseColumns),
    [baseColumns, rowActionsColumn],
  );

  // ─── Detail surface (#1392 D6) ─────────────────────────────────────────
  // `both` (default) = drawer + a full-page link in the drawer header;
  // `drawer` = drawer only; `page` = a row click navigates to the route.
  const [detailTarget, setDetailTarget] = useState<DetailTarget<T> | null>(null);
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  const surface = bundle.host.detail?.surface ?? 'both';

  // pagePath is row-aware (#1418): it receives the whole row so the target
  // can branch on a row field. The default derivation keys off the row's id.
  const detailPath = (item: T) => {
    const id = String(item[bundle.model.idField]);
    return bundle.host.detail?.pagePath?.(item) ?? `${location.pathname.replace(/\/+$/, '')}/${id}`;
  };

  const handleRowClick = (item: T) => {
    if (surface === 'page') {
      navigate(detailPath(item));
      return;
    }
    setDetailTarget({ id: String(item[bundle.model.idField]), listItem: item });
    setIsDetailOpen(true);
  };

  const handleDeleted = () => {
    // Deleting the last row on a page > 1 would otherwise strand the
    // operator on an out-of-range empty page. Step back one page so the
    // refetch lands on real rows (#1155).
    if (list.rows.length === 1 && list.page > 1) {
      list.setPage(list.page - 1);
    }
    setDetailTarget(null);
    setIsDetailOpen(false);
  };

  // ─── Create dialog ─────────────────────────────────────────────────────
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const canCreate = !!bundle.host.api.create && hasGrant(bundle.host.permission?.create);
  const canUpdate = !!bundle.host.api.update && hasGrant(bundle.host.permission?.update);
  const canDelete = !!bundle.host.api.delete && hasGrant(bundle.host.permission?.delete);

  // ─── PageHeader actions region ─────────────────────────────────────────
  // One right-aligned operation cluster on the title row: bespoke page CTAs
  // (`headerActions`), then the selection count + resource bulk actions, then
  // the generic create button.
  // The RESOLVED capability, not the raw declaration. Reading
  // `bundle.list.capabilities?.select` bypassed `resolveListCapabilities`
  // entirely, so an undeclared list silently got no selection no matter
  // what the source default said — the resolved set fed only SearchView.
  // A phone shows cards with no selection (#2680), so the bulk buttons —
  // which only act on a selection — go with it.
  const isPhone = useKitIsPhone();
  const selectable = list.capabilities.select && !isPhone;
  const hasBulkActions = !!selectable && bulk.actions.length > 0;
  const hasSelection = !!selectable && bulk.selectedRows.length > 0;
  const hasHeaderActions = headerActions != null || hasBulkActions || hasSelection || canCreate;
  const headerActionsNode = hasHeaderActions ? (
    <>
      {headerActions}
      {hasSelection && (
        // On phones the count pushed the bulk buttons off the title row; the
        // checkboxes already show the selection, so only screen readers hear it there.
        <span className="sr-only text-sm text-muted-foreground sm:not-sr-only" aria-live="polite">
          {t('rk.multiSelect.selected', { count: bulk.selectedRows.length })}
        </span>
      )}
      {hasBulkActions &&
        bulk.actions.map((action) => {
          const ineligible = bulk.isIneligible(action);
          return (
            <Button
              key={action.id}
              onClick={() => bulk.run(action)}
              disabled={bulk.selectedRows.length === 0 || ineligible || bulk.isPending}
              title={
                ineligible && action.disabledReason
                  ? t(action.disabledReason, { defaultValue: action.disabledReason })
                  : undefined
              }
              variant={action.intent === 'danger' ? 'destructive' : 'default'}
            >
              {t(action.label, { defaultValue: action.label })}
            </Button>
          );
        })}
      {canCreate && (
        <Button onClick={() => setIsCreateOpen(true)}>
          <Plus className="h-4 w-4 mr-1.5" />
          {bundle.host.formConfig?.createButtonLabel ??
            t('rk.resource.create', {
              name: t(bundle.host.label, { defaultValue: bundle.host.label }),
            })}
        </Button>
      )}
    </>
  ) : undefined;

  // ─── Quick filters (#1392 D4) ──────────────────────────────────────────
  const quickFiltersNode =
    bundle.host.quickFilters && bundle.host.quickFilters.length > 0 ? (
      <QuickFilters
        quickFilters={bundle.host.quickFilters}
        conditions={list.conditions}
        onChange={list.setFieldCondition}
      />
    ) : undefined;

  const presetSlot =
    list.presets && list.presets.length > 0 ? (
      // Preset quick-filters (#575) rendered inside the list toolbar so they
      // compose visually with the filter bar below them. Clicking a preset
      // writes conditions through the same plumbing, so the filter chips
      // reflect the active preset's predicate.
      <div className="flex flex-wrap items-center gap-2" data-testid="resource-presets">
        {list.presets.map((preset) => {
          const isActive = list.activePresetId === preset.id;
          return (
            <Button
              key={preset.id}
              size="sm"
              variant={isActive ? 'default' : 'outline'}
              onClick={() => list.togglePreset(preset)}
              data-preset-id={preset.id}
              data-preset-active={isActive}
            >
              {t(preset.label, { defaultValue: preset.label })}
              {preset.adornment?.()}
            </Button>
          );
        })}
      </div>
    ) : undefined;

  const title = t(bundle.host.listTitle ?? bundle.host.label, {
    defaultValue: bundle.host.listTitle ?? bundle.host.label,
  });
  const subtitle = bundle.host.listDescription
    ? t(bundle.host.listDescription, {
        defaultValue: bundle.host.listDescription,
      })
    : undefined;
  // #1416: optional header adornment driven by the resolved list total
  // (e.g. an inbox count badge).
  const titleAdornment = bundle.host.listTitleAdornment?.(list.pagination.total);

  const pageContent = (
    <>
      {bundle.host.listBanner?.()}
      {quickFiltersNode}
      <ListView
        key={bundle.host.slug}
        columns={columns}
        rows={list.rows}
        pagination={list.pagination}
        status={list.status}
        error={list.error}
        onRetry={list.retry}
        isFiltered={list.isFiltered}
        onClearFilters={list.clearFilters}
        droppedFilters={list.droppedFilters}
        // #1803: same-field conditions no longer collapse in
        // silence — whatever the wire layer merged (or
        // overrode) is said out loud above the table.
        mergeNotices={list.mergeNotices}
        emptyAction={
          canCreate ? (
            <Button size="sm" onClick={() => setIsCreateOpen(true)}>
              <Plus className="h-4 w-4 mr-1.5" />
              {bundle.host.formConfig?.createButtonLabel ??
                t('rk.resource.create', {
                  name: t(bundle.host.label, {
                    defaultValue: bundle.host.label,
                  }),
                })}
            </Button>
          ) : undefined
        }
        onPageChange={list.setPage}
        onLimitChange={list.setLimit}
        onSortChange={list.setSort}
        onSearchChange={list.setSearch}
        // Opt-in since #1642: the backend kernel 400s a free-text
        // `search` on every list route that declares no searchable
        // columns, so a box the resource did not ask for would
        // error. ListView's own default stays `true`.
        //
        // #1803: the controller resolves this now (design §6.6).
        // For a legacy `ResourceDefinition` it was `definition.searchable
        // ?? false` — identical to what this prop carried before,
        // asserted in `listSources.test.tsx`.
        searchable={list.capabilities.search}
        // #2114: the resolved `filter` capability had no consumer. A `remote`
        // list with no backend whitelist resolves to `filter: false` — every
        // condition it could build is dropped before the request — yet
        // `ListView`'s `capabilities?.filter ?? true` answered true for the
        // whole app because this prop was never passed. `search` is not
        // repeated here: it already travels on `searchable` above, and
        // `ListView` reads that as its own fallback.
        capabilities={{ filter: list.capabilities.filter }}
        onConditionsChange={list.setConditions}
        sortField={list.sortField}
        sortOrder={list.sortOrder}
        search={list.search}
        conditions={list.conditions}
        searchPlaceholder={
          bundle.host.searchPlaceholder
            ? t(bundle.host.searchPlaceholder, {
                defaultValue: bundle.host.searchPlaceholder,
              })
            : undefined
        }
        onRowClick={handleRowClick}
        selectable={selectable}
        // Gated on the resource's capability, not the phone-adjusted
        // `selectable`: when a resize to phone width turns selection off, the
        // table reports `[]` through this callback, so the bulk selection is
        // dropped rather than left acting on rows no checkbox shows (#2680).
        onSelectionChange={
          list.capabilities.select ? (rows) => bulk.setSelectedRows(rows as T[]) : undefined
        }
        selectionResetKey={bulk.selectionResetKey}
        // Always derive a stable row id from the business
        // `idField` (not just when selectable): index-based row
        // ids drift across background refetches, mis-attaching
        // per-row cell state to whatever row lands at the same
        // index. Keying on the id pins that state. (#1416)
        getRowId={(row) => String(row[bundle.model.idField])}
        fields={queryFields}
        queryBuilderColumns={queryBuilderColumns}
        filterable={bundle.host.filterable}
        exportFileName={bundle.host.slug}
        preferenceKey={bundle.host.listPreferenceKey}
        // A card title opens the detail PAGE when the resource has one, and
        // the drawer (through `onRowClick`) when it has only a drawer (#2680).
        getRowHref={surface === 'drawer' ? undefined : detailPath}
        renderCard={bundle.host.listCard}
        onExportAll={list.exportAll}
        // The selection's one truth lives here (the table
        // reports into `bulk.setSelectedRows`); ListView reads
        // it for the selection-scoped export rather than
        // keeping a copy.
        selectedRows={bulk.selectedRows}
        // Switch on the view, implementation on the host —
        // both required, mirroring select + bulk actions.
        print={list.capabilities.print ? bundle.host.print : undefined}
        presetSlot={presetSlot}
      />

      <ResourceDetailSurface
        bundle={bundle}
        columns={detailColumns}
        target={detailTarget}
        open={isDetailOpen}
        onOpenChange={setIsDetailOpen}
        detailPath={detailPath}
        canUpdate={canUpdate}
        canDelete={canDelete}
        onDeleted={handleDeleted}
      />

      {canCreate && isCreateOpen && (
        <ResourceFormDialog bundle={bundle} onClose={() => setIsCreateOpen(false)} />
      )}
    </>
  );

  const page = container ? (
    <PageShell
      title={title}
      subtitle={subtitle}
      titleAdornment={titleAdornment}
      actions={headerActionsNode}
      tabs={bundle.host.listTabs?.()}
    >
      {pageContent}
    </PageShell>
  ) : (
    <div className="space-y-6">
      <PageHeader
        title={title}
        description={subtitle}
        titleAdornment={titleAdornment}
        actions={headerActionsNode}
      />
      {bundle.host.listTabs?.()}
      {pageContent}
    </div>
  );

  return (
    <RowBusyProvider handle={rowBusy}>
      <ResourceErrorBoundary
        fallbackTitle={t('rk.resource.errorLoading', {
          name: t(bundle.host.label, { defaultValue: bundle.host.label }),
        })}
        onRetry={() => {
          // The list query lives ABOVE this boundary, so it stays
          // mounted while the boundary shows its error card; the
          // controller's retry evicts the poisoned rows and refetches.
          queryClient.removeQueries({ queryKey: resourceKeys(bundle.host.slug).all() });
          list.retry();
        }}
      >
        {page}
      </ResourceErrorBoundary>
    </RowBusyProvider>
  );
}
